import React, { useEffect, useRef, useState } from 'react';
import TermsAndConditionsContent, { TERMS_LAST_UPDATED_LABEL } from './termsAndConditionsContent';

interface TermsAcceptanceModalProps {
  onAccept: () => void;
  // Omit to make the modal non-dismissible (used to gate access for
  // already-logged-in users who haven't accepted the current version yet).
  onClose?: () => void;
  loading?: boolean;
  error?: string | null;
  title?: string;
  acceptLabel?: string;
  // Defaults to the Terms and Conditions content -- overridden by
  // PrivacyAcceptanceModal.tsx to reuse this same shell (scroll-to-bottom
  // gating, layout, accept/cancel buttons) for the Privacy Policy instead.
  content?: React.ReactNode;
  lastUpdatedLabel?: string;
}

export default function TermsAcceptanceModal({
  onAccept,
  onClose,
  loading = false,
  error = null,
  title = 'Términos y Condiciones',
  acceptLabel = 'Aceptar y continuar',
  content = <TermsAndConditionsContent />,
  lastUpdatedLabel = TERMS_LAST_UPDATED_LABEL,
}: TermsAcceptanceModalProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // If the content already fits without scrolling (e.g. a very tall
  // viewport), don't leave the accept button permanently disabled.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 4) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      setHasScrolledToBottom(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-1">Última actualización: {lastUpdatedLabel}</p>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Cerrar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto p-6 flex-1"
        >
          {content}
        </div>

        <div className="p-6 pt-4 border-t border-gray-200">
          {!hasScrolledToBottom && (
            <p className="text-xs text-gray-500 mb-3 text-center">
              Desplázate hasta el final para poder aceptar.
            </p>
          )}
          {error && <p className="text-sm text-red-600 mb-3 text-center">{error}</p>}
          <div className="flex gap-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="button"
              onClick={onAccept}
              disabled={!hasScrolledToBottom || loading}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : acceptLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
